'use client'
import Image from 'next/image'

const LoadingCard = ({ cardStyle }) => {
    const card   = cardStyle?.card
    const fields = cardStyle?.fields ?? []

    const imageRatio   = card?.imageRatio    ?? '4/3'
    const imageRadius  = card?.imageRadius   ?? 8
    const cardBg       = card?.bg            ?? '#ffffff'
    const cardRadius   = card?.radius        ?? 8
    const cardBorder   = card?.borderWidth > 0 ? `${card.borderWidth}px solid ${card.borderColor}` : 'none'
    const cardShadow   = card?.shadow        ?? 'none'
    const contentPad   = card?.contentPadding ?? '12px'

    // Build skeleton rows from enabled fields (excluding label which is on image)
    const skeletonFields = fields.filter(f =>
        f.enabled && !f.isDivider && f.id !== 'label' && !f.showOnImage
    )

    return (
        <div style={{
            width: '100%',
            background: cardBg,
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            overflow: 'hidden',
        }}>
            {/* Image skeleton */}
            <div style={{
                width: '100%',
                aspectRatio: imageRatio,
                borderRadius: imageRadius,
                overflow: 'hidden',
                background: '#e5e7eb',
                position: 'relative',
            }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.4s infinite',
                }} />
            </div>

            {/* Content skeleton — mirrors real field rows */}
            <div style={{ padding: contentPad, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {skeletonFields.map((field, i) => {
                    // Divider
                    if (field.isDivider) {
                        return <div key={i} style={{ height: 1, background: '#e5e7eb', margin: '2px 0' }} />
                    }

                    // Specs row — render 3 pill skeletons side by side
                    if (field.isSpecs) {
                        return (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {[40, 48, 52].map((w, j) => (
                                    <SkeletonBar key={j} width={w} height={field.fontSize ? field.fontSize * 14 : 13} />
                                ))}
                            </div>
                        )
                    }

                    // Transit row
                    if (field.isTransit) {
                        return (
                            <div key={i} style={{ display: 'flex', gap: 6 }}>
                                <SkeletonBar width={72} height={22} radius={20} />
                                <SkeletonBar width={60} height={22} radius={20} />
                            </div>
                        )
                    }

                    // Name — taller, two lines
                    if (field.id === 'name') {
                        const lineH   = (field.fontSize ?? 1) * 16 * 1.3
                        const clamp   = field.lineClamp ?? 2
                        const totalH  = lineH * clamp + (clamp - 1) * 4
                        return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <SkeletonBar width="90%" height={lineH} />
                                {clamp >= 2 && <SkeletonBar width="65%" height={lineH} />}
                            </div>
                        )
                    }

                    // Price — slightly wider
                    if (field.id === 'price') {
                        return <SkeletonBar key={i} width="55%" height={(field.fontSize ?? 1) * 16} />
                    }

                    // Generic single-line field
                    const h = Math.round((field.fontSize ?? 0.8) * 16)
                    return <SkeletonBar key={i} width="70%" height={h} />
                })}
            </div>

            <style>{`
                @keyframes shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
        </div>
    )
}

// Tiny reusable shimmer bar
function SkeletonBar({ width, height = 14, radius = 4 }) {
    return (
        <div style={{
            width: typeof width === 'number' ? `${width}px` : width,
            height,
            borderRadius: radius,
            background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s infinite',
            flexShrink: 0,
        }} />
    )
}

export default LoadingCard